import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { bloodTestsAPI } from '../services/api'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine
} from 'recharts'
import { useChartTheme } from '../hooks/useChartTheme'
import { buildTrendPoints, trendChangePercent } from '../components/blood-tests/markerValueFormat'

// VH-221: a censored point is drawn at its limit as a hollow triangle that
// points the way the true value lies ("above 52" points up).
export function TrendDot({ cx, cy, payload, stroke }) {
  if (cx === undefined || cy === undefined || cx === null || cy === null) return null
  if (payload?.censored) {
    const up = payload.operator === '>' || payload.operator === '>='
    const d = up
      ? `M${cx},${cy - 7} L${cx - 6},${cy + 5} L${cx + 6},${cy + 5} Z`
      : `M${cx},${cy + 7} L${cx - 6},${cy - 5} L${cx + 6},${cy - 5} Z`
    return <path d={d} fill="none" stroke={stroke} strokeWidth={2} data-testid="censored-dot" />
  }
  return <circle cx={cx} cy={cy} r={5} fill={stroke} stroke={stroke} data-testid="value-dot" />
}

export default function TrendAnalysis() {
  // VH-133: recharts takes colours as props, so it inherits nothing from
  // the design system — these follow the active theme instead.
  const chart = useChartTheme()
  const { t } = useTranslation()
  const { id } = useParams()

  const { data, isLoading } = useQuery({
    queryKey: ['bloodTestTrends', id],
    queryFn: async () => {
      const response = await bloodTestsAPI.getTrends(id)
      return response.data
    },
  })

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  const markersWithHistory = data?.markers?.filter(m => m.historical_values && m.historical_values.length >= 2) || []

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link to={`/blood-tests/${id}`} className="text-primary-600 hover:text-primary-700 text-sm mb-2 inline-block">
          ← Back to Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Marker Trends Over Time</h1>
        <p className="text-gray-600 mt-1">Visualize how your blood markers have changed over time</p>
      </div>

      {markersWithHistory.length === 0 ? (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                Not enough data to show trends. Upload at least 2 blood tests with the same markers to see trends.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {markersWithHistory.map((marker) => {
            // Format data for Recharts
            const chartData = buildTrendPoints(marker.historical_values, t)
            const firstPoint = chartData[0]
            const lastPoint = chartData[chartData.length - 1]
            const changePercent = trendChangePercent(firstPoint, lastPoint)
            const hasCensored = chartData.some(p => p.censored)

            // Calculate reference range lines
            const refRange = marker.reference_range
            let minRange = null
            let maxRange = null

            if (refRange && refRange.includes('-')) {
              const [min, max] = refRange.split('-').map(v => parseFloat(v.trim()))
              if (!isNaN(min)) minRange = min
              if (!isNaN(max)) maxRange = max
            }

            return (
              <div key={marker.marker_id} className="card">
                <div className="card-header">
                  <h2 className="text-xl font-semibold">{marker.marker_name}</h2>
                  {marker.reference_range && (
                    <p className="text-sm text-gray-600 mt-1">
                      Reference Range: {marker.reference_range} {marker.unit}
                    </p>
                  )}
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                      <XAxis dataKey="date" />
                      <YAxis label={{ value: marker.unit, angle: -90, position: 'insideLeft' }} />
                      <Tooltip
                        formatter={(value, _name, item) => [
                          item?.payload?.censored
                            ? `${item.payload.label} ${marker.unit} (${t('bloodTests.context.censoredPoint')})`
                            : `${value} ${marker.unit}`,
                          'Value',
                        ]}
                      />
                      <Legend />

                      {/* Reference range lines */}
                      {minRange !== null && (
                        <ReferenceLine
                          y={minRange}
                          stroke={chart.success}
                          strokeDasharray="3 3"
                          label="Min Normal"
                        />
                      )}
                      {maxRange !== null && (
                        <ReferenceLine
                          y={maxRange}
                          stroke={chart.success}
                          strokeDasharray="3 3"
                          label="Max Normal"
                        />
                      )}

                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={chart.info}
                        strokeWidth={2}
                        dot={<TrendDot />}
                        connectNulls
                        activeDot={{ r: 7 }}
                        name={marker.marker_name}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  {hasCensored && (
                    <p className="mt-2 text-xs text-gray-500">△ {t('bloodTests.context.censoredPoint')}</p>
                  )}

                  {/* Trend Summary */}
                  <div className="mt-4 p-3 bg-gray-50 rounded">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Latest</p>
                        <p className="font-semibold">
                          {lastPoint?.label} {marker.unit}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Earliest</p>
                        <p className="font-semibold">
                          {firstPoint?.label} {marker.unit}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Change</p>
                        <p className={`font-semibold ${
                          changePercent === null ? 'text-gray-500'
                            : changePercent > 0 ? 'text-danger-600' : 'text-success-600'
                        }`}>
                          {changePercent === null ? '—' : `${changePercent.toFixed(1)}%`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
